import jeniferPhoto from "@/assets/jenifer-portrait.webp";
import { BLOG_POSTS, blogSlugFromPath, getCachedBlogPost, postsForRoute } from "@/blog/data";
import { BLOG_ROUTE_BY_ID, BLOG_ROUTES, blogPostPath, blogRoutePath } from "@/blog/routes";
import type { BlogPost, BlogPostSummary, BlogRouteId } from "@/blog/types";
import { buildFaqPageNode, getFaqsForPath } from "@/config/aeo";
import { SHOW_SISTEMA_LUCENTIA } from "@/config/features";
import { ADVISORY_FORM_PATH, LINK_HUB_PATH, QUIZ_PATH, SOCIAL } from "@/config/constants";
import type { AppLocale } from "@/i18n/constants";
import { getContent } from "@/data/content";
import {
  LEGAL_ADDRESS,
  LEGAL_CONTACT_EMAIL,
  LEGAL_MAPS_URL,
  LEGAL_PHONE_E164,
  LEGAL_SLUG_BY_PATH,
  type LegalPageContent,
} from "@/data/legal.types";
import { NICHE_PATHS, nicheByPath } from "@/data/niches";
import { getPricing, PRICES_INCLUDE_VAT, type PricingPack } from "@/data/pricing";
import {
  SEO_AREA_SERVED_EN,
  SEO_HOME_EN,
  SEO_INSTAGRAM_ADVISORY_EN,
  SEO_INSTAGRAM_MANAGEMENT_EN,
  SEO_LEGAL_EN,
  SEO_METHOD_EN,
  SEO_NOT_FOUND_EN,
  SEO_PERSON_JOB_TITLE_EN,
  SEO_PRICING_EN,
  SEO_RESOURCES_EN,
  SEO_SERVICE_NAME_EN,
  SEO_SERVICE_TYPES_EN,
  SEO_WEB_SERVICE_EN,
} from "./seo.en";
import {
  SEO_ANALYTICS,
  SEO_AREA_SERVED_ES,
  SEO_BLOG_ES,
  SEO_HOME_ES,
  SEO_INSTAGRAM_ADVISORY_ES,
  SEO_INSTAGRAM_MANAGEMENT_ES,
  SEO_LEGAL_ES,
  SEO_METHOD_ES,
  SEO_NOT_FOUND_ES,
  SEO_PERSON_JOB_TITLE_ES,
  SEO_PRICING_ES,
  SEO_RESOURCES_ES,
  SEO_SERVICE_NAME_ES,
  SEO_SERVICE_TYPES_ES,
  SEO_WEB_SERVICE_ES,
} from "./seo.es";

export { SEO_ANALYTICS };

type JsonLd = Record<string, unknown>;

export type PageSeo = {
  title: string;
  description: string;
  path: string;
  keywords?: string;
  robots?: string;
  imageUrl?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  ogType?: "website" | "article";
  article?: { publishedTime: string; modifiedTime: string; section?: string; tags?: readonly string[] };
  jsonLd?: JsonLd;
};

/** Public routes written to static HTML at build time (Spanish). */
const PRERENDER_ROUTE_LIST = [
  "/",
  "/metodo",
  "/precios",
  "/gestion-instagram",
  "/asesoria-instagram",
  ADVISORY_FORM_PATH,
  QUIZ_PATH,
  LINK_HUB_PATH,
  ...NICHE_PATHS,
  "/pagina-web",
  "/recursos",
  "/recursos/compra",
  "/recursos/mis-compras",
  "/aviso-legal",
  "/privacidad",
  "/cookies",
  "/condiciones",
  "/accesibilidad",
] as const;

export const PRERENDER_ROUTES = SHOW_SISTEMA_LUCENTIA
  ? PRERENDER_ROUTE_LIST
  : PRERENDER_ROUTE_LIST.filter((path) => path !== "/metodo");

const OG_IMAGE = {
  imageUrl: "/og-image.jpg",
  imageWidth: 1200,
  imageHeight: 630,
};

function inLanguage(locale: AppLocale): string[] {
  return locale === "en" ? ["en", "en-GB"] : ["es-ES", "es"];
}

function areaServed(locale: AppLocale) {
  return locale === "en" ? SEO_AREA_SERVED_EN : SEO_AREA_SERVED_ES;
}

function homeCrumb(locale: AppLocale): string {
  return locale === "en" ? SEO_HOME_EN.breadcrumbHome : SEO_HOME_ES.breadcrumbHome;
}

function postalAddress() {
  return {
    "@type": "PostalAddress",
    streetAddress: LEGAL_ADDRESS.street,
    postalCode: LEGAL_ADDRESS.postalCode,
    addressLocality: LEGAL_ADDRESS.city,
    addressRegion: LEGAL_ADDRESS.region,
    addressCountry: LEGAL_ADDRESS.countryCode,
  };
}

function webPageNode(origin: string, locale: AppLocale, seo: PageSeo): JsonLd {
  const url = `${origin}${seo.path}`;
  return {
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    name: seo.title,
    description: seo.description,
    url,
    isPartOf: { "@id": `${origin}/#website` },
    about: { "@id": `${origin}/#business` },
    inLanguage: inLanguage(locale),
  };
}

function breadcrumbNode(origin: string, locale: AppLocale, name: string, path: string): JsonLd {
  const url = `${origin}${path}`;
  return {
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: homeCrumb(locale), item: `${origin}/` },
      { "@type": "ListItem", position: 2, name, item: url },
    ],
  };
}

function trailNode(origin: string, locale: AppLocale, trail: { name: string; path: string }[]): JsonLd {
  const url = `${origin}${trail[trail.length - 1].path}`;
  return {
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: [{ name: homeCrumb(locale), path: "/" }, ...trail].map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${origin}${item.path === "/" ? "/" : item.path}`,
    })),
  };
}

function absoluteUrl(origin: string, url: string): string {
  return /^https?:\/\//.test(url) ? url : `${origin}${url}`;
}

function blogPostingRef(origin: string, post: BlogPostSummary): JsonLd {
  const url = `${origin}${blogPostPath(post.slug)}`;
  return {
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    headline: post.title,
    url,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    ...(post.coverUrl ? { image: absoluteUrl(origin, post.coverUrl) } : {}),
  };
}

function blogIndexSeo(origin: string, locale: AppLocale): PageSeo {
  const s = SEO_BLOG_ES;
  const seo: PageSeo = {
    ...OG_IMAGE,
    title: s.title,
    description: s.description,
    path: s.path,
    keywords: s.keywords,
    imageAlt: s.ogImageAlt,
    /* An empty blog stays out of Google until the first article is published. */
    robots: BLOG_POSTS.length ? undefined : "noindex, follow",
  };
  return {
    ...seo,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        { ...webPageNode(origin, locale, seo), "@type": "CollectionPage" },
        trailNode(origin, locale, [{ name: s.breadcrumb, path: s.path }]),
        {
          "@type": "Blog",
          "@id": `${origin}/blog#blog`,
          name: s.name,
          description: s.description,
          url: `${origin}/blog`,
          inLanguage: "es-ES",
          publisher: { "@id": `${origin}/#business` },
          author: { "@id": `${origin}/#person` },
          blogPost: BLOG_POSTS.map((post) => blogPostingRef(origin, post)),
        },
      ],
    },
  };
}

function blogRouteSeo(origin: string, locale: AppLocale, id: BlogRouteId): PageSeo {
  const route = BLOG_ROUTE_BY_ID[id];
  const path = blogRoutePath(id);
  const posts = postsForRoute(id);
  const seo: PageSeo = {
    ...OG_IMAGE,
    title: `${route.title} | Blog Lucentia`,
    description: route.description,
    path,
    keywords: `${route.label}, blog, negocios con cita, Lucentia Social`,
    imageAlt: route.title,
    robots: posts.length ? undefined : "noindex, follow",
  };
  return {
    ...seo,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        { ...webPageNode(origin, locale, seo), "@type": "CollectionPage", isPartOf: { "@id": `${origin}/blog#blog` } },
        trailNode(origin, locale, [
          { name: SEO_BLOG_ES.breadcrumb, path: "/blog" },
          { name: `Ruta ${route.label}`, path },
        ]),
        {
          "@type": "ItemList",
          "@id": `${origin}${path}#list`,
          name: route.title,
          itemListOrder: "https://schema.org/ItemListOrderAscending",
          itemListElement: posts.map((post, index) => ({
            "@type": "ListItem",
            position: index + 1,
            url: `${origin}${blogPostPath(post.slug)}`,
            name: post.title,
          })),
        },
      ],
    },
  };
}

function blogPostSeo(origin: string, locale: AppLocale, post: BlogPost): PageSeo {
  const route = BLOG_ROUTE_BY_ID[post.route];
  const path = blogPostPath(post.slug);
  const url = `${origin}${path}`;
  const suffix = " | Lucentia Social";
  const title = post.seoTitle || (post.title.length + suffix.length <= 62 ? `${post.title}${suffix}` : post.title);
  const description = post.seoDescription || post.excerpt;
  const tags = [post.keyword, ...post.sectors].filter(Boolean);
  const seo: PageSeo = {
    ...(post.coverUrl ? { imageUrl: post.coverUrl } : OG_IMAGE),
    title,
    description,
    path,
    keywords: tags.join(", ") || undefined,
    imageAlt: post.coverUrl ? post.coverAlt : post.title,
    robots: post.noindex ? "noindex, follow" : undefined,
    ogType: "article",
    article: { publishedTime: post.publishedAt, modifiedTime: post.updatedAt, section: route.label, tags },
  };

  const faq: JsonLd | null = post.faqs.length
    ? {
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        url,
        mainEntity: post.faqs.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      }
    : null;

  const nodes: JsonLd[] = [
    { ...webPageNode(origin, locale, seo), isPartOf: { "@id": `${origin}/blog#blog` } },
    trailNode(origin, locale, [
      { name: SEO_BLOG_ES.breadcrumb, path: "/blog" },
      { name: `Ruta ${route.label}`, path: blogRoutePath(post.route) },
      { name: post.title, path },
    ]),
    {
      ...blogPostingRef(origin, post),
      description,
      image: post.coverUrl ? absoluteUrl(origin, post.coverUrl) : `${origin}${OG_IMAGE.imageUrl}`,
      author: { "@id": `${origin}/#person` },
      publisher: { "@id": `${origin}/#business` },
      mainEntityOfPage: { "@id": `${url}#webpage` },
      isPartOf: { "@id": `${origin}/blog#blog` },
      articleSection: route.label,
      keywords: tags,
      wordCount: post.wordCount,
      timeRequired: `PT${post.readingMinutes}M`,
      inLanguage: "es-ES",
      ...(post.quickAnswer ? { abstract: post.quickAnswer } : {}),
    },
  ];
  if (faq) nodes.push(faq);
  return { ...seo, jsonLd: { "@context": "https://schema.org", "@graph": nodes } };
}

function packOffer(origin: string, locale: AppLocale, pack: PricingPack): JsonLd {
  const serviceName =
    locale === "en" ? `Instagram management — ${pack.name}` : `Gestión de Instagram — Pack ${pack.name}`;
  return {
    "@type": "Offer",
    "@id": `${origin}/precios#${pack.id}`,
    name: locale === "en" ? `${pack.name} pack` : `Pack ${pack.name}`,
    description: pack.idealFor,
    url: `${origin}/precios#${pack.id}`,
    price: pack.price,
    priceCurrency: "EUR",
    priceSpecification: {
      "@type": "UnitPriceSpecification",
      price: pack.price,
      priceCurrency: "EUR",
      valueAddedTaxIncluded: PRICES_INCLUDE_VAT,
      ...(pack.cadence === "month" ? { unitCode: "MON", referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "MON" } } : {}),
    },
    itemOffered: {
      "@type": "Service",
      name: serviceName,
      description: pack.includes.join(". "),
      provider: { "@id": `${origin}/#business` },
    },
    seller: { "@id": `${origin}/#business` },
  };
}

function businessGraph(origin: string, locale: AppLocale): JsonLd[] {
  const seoHome = locale === "en" ? SEO_HOME_EN : SEO_HOME_ES;
  const sameAs = [SOCIAL.instagram.split("?")[0], SOCIAL.tiktok];
  const serviceTypes = locale === "en" ? SEO_SERVICE_TYPES_EN : SEO_SERVICE_TYPES_ES;
  const logo = `${origin}/logo.png`;
  const personImage = jeniferPhoto.startsWith("http") ? jeniferPhoto : `${origin}${jeniferPhoto}`;
  const pricing = getPricing(locale);
  const content = getContent(locale);

  return [
    {
      "@type": "WebSite",
      "@id": `${origin}/#website`,
      url: `${origin}/`,
      name: "Lucentia Social",
      description: seoHome.description,
      inLanguage: inLanguage(locale),
      publisher: { "@id": `${origin}/#business` },
    },
    {
      "@type": ["ProfessionalService", "Organization"],
      "@id": `${origin}/#business`,
      name: "Lucentia Social",
      alternateName: locale === "en" ? SEO_SERVICE_NAME_EN : SEO_SERVICE_NAME_ES,
      description: seoHome.description,
      url: `${origin}/`,
      logo: { "@type": "ImageObject", url: logo, width: 440, height: 379 },
      image: logo,
      email: LEGAL_CONTACT_EMAIL,
      telephone: LEGAL_PHONE_E164,
      address: postalAddress(),
      hasMap: LEGAL_MAPS_URL,
      areaServed: areaServed(locale),
      priceRange: "130 € – 550 €",
      currenciesAccepted: "EUR",
      paymentAccepted: "Bizum, PayPal, Cash",
      openingHoursSpecification: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "18:00",
      },
      founder: { "@id": `${origin}/#person` },
      sameAs,
      knowsAbout: [...serviceTypes],
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        email: LEGAL_CONTACT_EMAIL,
        telephone: LEGAL_PHONE_E164,
        url: SOCIAL.whatsappDirect,
        availableLanguage: ["Spanish", "English"],
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: locale === "en" ? "Lucentia Social services" : "Servicios de Lucentia Social",
        itemListElement: [
          ...pricing.packs.map((pack) => packOffer(origin, locale, pack)),
          ...pricing.services.items.map((item) => ({
            "@type": "Offer",
            "@id": `${origin}/precios#${item.id}`,
            name: item.title,
            description: item.desc,
            url: `${origin}${item.href ?? "/precios"}`,
            priceCurrency: "EUR",
            priceSpecification: {
              "@type": item.from ? "PriceSpecification" : "UnitPriceSpecification",
              ...(item.from ? { minPrice: item.price } : { price: item.price }),
              priceCurrency: "EUR",
              valueAddedTaxIncluded: PRICES_INCLUDE_VAT,
            },
            ...(item.from ? {} : { price: item.price }),
            itemOffered: { "@type": "Service", name: item.title, provider: { "@id": `${origin}/#business` } },
            seller: { "@id": `${origin}/#business` },
          })),
          ...content.services.map((service) => ({
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: service.title,
              description: service.desc,
              url: service.href ? `${origin}${service.href}` : `${origin}/`,
            },
          })),
        ],
      },
    },
    {
      "@type": "Person",
      "@id": `${origin}/#person`,
      name: "Jenifer Roelstraete",
      jobTitle: locale === "en" ? SEO_PERSON_JOB_TITLE_EN : SEO_PERSON_JOB_TITLE_ES,
      image: personImage,
      url: `${origin}/#sobre-mi`,
      sameAs,
      worksFor: { "@id": `${origin}/#business` },
      knowsAbout: [...serviceTypes],
    },
  ];
}

function withFaq(nodes: JsonLd[], origin: string, locale: AppLocale, path: string): JsonLd {
  const faq = buildFaqPageNode(`${origin}${path}`, getFaqsForPath(locale, path));
  return { "@context": "https://schema.org", "@graph": faq ? [...nodes, faq] : nodes };
}

function servicePage(
  origin: string,
  locale: AppLocale,
  seo: PageSeo,
  crumb: string,
  service: { name: string; serviceType: string; audience?: readonly string[]; offers?: JsonLd[] },
): PageSeo {
  const url = `${origin}${seo.path}`;
  return {
    ...seo,
    jsonLd: withFaq(
      [
        webPageNode(origin, locale, seo),
        breadcrumbNode(origin, locale, crumb, seo.path),
        {
          "@type": "Service",
          "@id": `${url}#service`,
          name: service.name,
          description: seo.description,
          url,
          serviceType: service.serviceType,
          provider: { "@id": `${origin}/#business` },
          areaServed: areaServed(locale),
          ...(service.audience
            ? { audience: { "@type": "BusinessAudience", audienceType: service.audience.join(", ") } }
            : {}),
          ...(service.offers ? { offers: service.offers } : {}),
        },
      ],
      origin,
      locale,
      seo.path,
    ),
  };
}

/**
 * SEO for a route. Legal pages pass their own content so the legal texts stay
 * out of the shared bundle (they load with the legal page).
 */
export function getPageSeo(
  path: string,
  locale: AppLocale,
  origin: string,
  legalPage?: LegalPageContent,
  blogPost?: BlogPost,
): PageSeo {
  const en = locale === "en";

  const niche = nicheByPath(path);
  if (niche) {
    const copy = niche[locale];
    const seo: PageSeo = {
      ...OG_IMAGE,
      title: copy.seo.title,
      description: copy.seo.description,
      path,
      keywords: copy.seo.keywords,
      imageAlt: copy.hero.title,
    };
    return servicePage(origin, locale, seo, copy.name, {
      name: copy.hero.title,
      serviceType: en ? "Digital marketing and Instagram management" : "Marketing digital y gestión de Instagram",
      audience: [copy.name],
    });
  }

  /* The blog is written in Spanish only; its metadata stays Spanish. */
  if (path === "/blog") return blogIndexSeo(origin, locale);
  const routeMatch = /^\/blog\/ruta\/([a-z]+)$/.exec(path);
  const routeId = BLOG_ROUTES.find((route) => route.id === routeMatch?.[1])?.id;
  if (routeId) return blogRouteSeo(origin, locale, routeId);
  const slug = blogSlugFromPath(path);
  const post = blogPost ?? (slug ? getCachedBlogPost(slug) : undefined);
  if (post) return blogPostSeo(origin, locale, post);

  const content = getContent(locale);
  const pricing = getPricing(locale);

  switch (path) {
    case "/": {
      const s = en ? SEO_HOME_EN : SEO_HOME_ES;
      const seo: PageSeo = { ...OG_IMAGE, title: s.title, description: s.description, path: "/", keywords: s.keywords, imageAlt: s.ogImageAlt };
      return {
        ...seo,
        jsonLd: withFaq(
          [
            ...businessGraph(origin, locale),
            {
              ...webPageNode(origin, locale, seo),
              "@type": ["WebPage", "AboutPage"],
              speakable: { "@type": "SpeakableSpecification", cssSelector: ["#hero-heading", "#faq-heading"] },
            },
          ],
          origin,
          locale,
          "/",
        ),
      };
    }
    case "/metodo": {
      if (!SHOW_SISTEMA_LUCENTIA) break;
      const s = en ? SEO_METHOD_EN : SEO_METHOD_ES;
      const seo: PageSeo = { ...OG_IMAGE, title: s.title, description: s.description, path: s.path, keywords: s.keywords, imageAlt: s.ogImageAlt };
      const method = content.methodPage;
      return {
        ...seo,
        jsonLd: withFaq(
          [
            webPageNode(origin, locale, seo),
            breadcrumbNode(origin, locale, s.breadcrumbMethod, s.path),
            {
              "@type": "HowTo",
              "@id": `${origin}${s.path}#howto`,
              name: method.hero.title,
              description: method.hero.subtitle,
              step: method.process.steps.map((step, index) => ({
                "@type": "HowToStep",
                position: index + 1,
                name: step.title,
                text: [step.description, ...step.items].join(" "),
              })),
            },
          ],
          origin,
          locale,
          s.path,
        ),
      };
    }
    case "/precios": {
      const s = en ? SEO_PRICING_EN : SEO_PRICING_ES;
      const seo: PageSeo = { ...OG_IMAGE, title: s.title, description: s.description, path: s.path, keywords: s.keywords, imageAlt: s.ogImageAlt };
      const monthly = pricing.packs.filter((pack) => pack.cadence === "month");
      return servicePage(origin, locale, seo, s.breadcrumb, {
        name: en ? "Strategic Instagram Management" : "Gestión Estratégica de Instagram",
        serviceType: "Community management",
        offers: [
          {
            "@type": "AggregateOffer",
            priceCurrency: "EUR",
            lowPrice: Math.min(...monthly.map((pack) => pack.price)),
            highPrice: Math.max(...monthly.map((pack) => pack.price)),
            offerCount: pricing.packs.length,
            offers: pricing.packs.map((pack) => packOffer(origin, locale, pack)),
          },
        ],
      });
    }
    case "/gestion-instagram": {
      const s = en ? SEO_INSTAGRAM_MANAGEMENT_EN : SEO_INSTAGRAM_MANAGEMENT_ES;
      const seo: PageSeo = { ...OG_IMAGE, title: s.title, description: s.description, path: s.path, keywords: s.keywords, imageAlt: s.ogImageAlt };
      return servicePage(origin, locale, seo, s.breadcrumb, {
        name: en ? "Strategic Instagram Management" : "Gestión Estratégica de Instagram",
        serviceType: "Community management",
        audience: content.instagramManagementPage.audience.items,
        offers: pricing.packs.map((pack) => packOffer(origin, locale, pack)),
      });
    }
    case "/asesoria-instagram": {
      const s = en ? SEO_INSTAGRAM_ADVISORY_EN : SEO_INSTAGRAM_ADVISORY_ES;
      const seo: PageSeo = { ...OG_IMAGE, title: s.title, description: s.description, path: s.path, keywords: s.keywords, imageAlt: s.ogImageAlt };
      return servicePage(origin, locale, seo, s.breadcrumb, {
        name: en ? "Strategic Instagram Advisory" : "Asesoría Estratégica de Instagram",
        serviceType: en ? "Instagram strategy advisory" : "Asesoría de estrategia de Instagram",
      });
    }
    case "/pagina-web": {
      const s = en ? SEO_WEB_SERVICE_EN : SEO_WEB_SERVICE_ES;
      const seo: PageSeo = { ...OG_IMAGE, title: s.title, description: s.description, path: s.path, keywords: s.keywords, imageAlt: s.ogImageAlt };
      return servicePage(origin, locale, seo, s.breadcrumbWeb, {
        name: en ? "Strategic Website" : "Página Web Estratégica",
        serviceType: en ? "Website design" : "Diseño web",
        audience: content.webServicePage.audience.items,
      });
    }
    case "/recursos": {
      const s = en ? SEO_RESOURCES_EN : SEO_RESOURCES_ES;
      const seo: PageSeo = { ...OG_IMAGE, title: s.title, description: s.description, path: s.path, keywords: s.keywords, imageAlt: s.itemListName };
      return {
        ...seo,
        jsonLd: {
          "@context": "https://schema.org",
          "@graph": [webPageNode(origin, locale, seo), breadcrumbNode(origin, locale, s.breadcrumbResources, s.path)],
        },
      };
    }
    case "/recursos/compra":
      /* Thank-you page after Stripe Checkout: personal, never in search results. */
      return {
        title: en ? "Your purchase | Lucentia Social" : "Tu compra | Lucentia Social",
        description: en
          ? "Download the resources you bought from Lucentia Social."
          : "Descarga los recursos que compraste en Lucentia Social.",
        path,
        robots: "noindex, nofollow",
      };
    case "/recursos/mis-compras":
      return {
        title: en ? "My purchases | Lucentia Social" : "Mis compras | Lucentia Social",
        description: en
          ? "Get the download links for the resources you bought, sent to your email."
          : "Recibe en tu email los enlaces de descarga de los recursos que compraste.",
        path,
        robots: "noindex, follow",
      };
    case QUIZ_PATH: {
      const seo: PageSeo = {
        ...OG_IMAGE,
        title: en
          ? "Quiz: does your Instagram fill your diary? | Lucentia Social"
          : "Test: ¿tu Instagram llena tu agenda? | Lucentia Social",
        description: en
          ? "Answer 9 questions in 2 minutes and see your score, your weakest point and what to do first to get more bookings from Instagram."
          : "Respondé 9 preguntas en 2 minutos y descubrí tu puntuación, tu punto más flojo y qué hacer primero para conseguir más reservas desde Instagram.",
        path,
        keywords: en
          ? "Instagram quiz, Instagram for appointment businesses, more bookings Instagram"
          : "test Instagram, Instagram para negocios con reservas, más reservas Instagram, auditoría Instagram",
        imageAlt: en ? "Instagram quiz — Lucentia Social" : "Test de Instagram — Lucentia Social",
      };
      return {
        ...seo,
        jsonLd: {
          "@context": "https://schema.org",
          "@graph": [webPageNode(origin, locale, seo), breadcrumbNode(origin, locale, en ? "Instagram quiz" : "Test de Instagram", path)],
        },
      };
    }
    case LINK_HUB_PATH:
      /* The Instagram bio hub duplicates the site's own links: keep it out of search. */
      return {
        ...OG_IMAGE,
        title: "Jenifer Roelstraete | Lucentia Social",
        description: en
          ? "Free audit, Instagram quiz, plans and services from Lucentia Social."
          : "Auditoría gratuita, test de Instagram, planes y servicios de Lucentia Social.",
        path,
        robots: "noindex, follow",
      };
    case ADVISORY_FORM_PATH:
      /* An intake step before the calendar: useful to visitors, not to search results. */
      return {
        title: en
          ? "Book the Strategic Instagram Advisory | Lucentia Social"
          : "Reservar la Asesoría Estratégica de Instagram | Lucentia Social",
        description: en
          ? "Answer a few questions about your business so Jenifer can study your account, then choose a time for your strategic advisory."
          : "Respondé unas preguntas sobre tu negocio para que Jenifer analice tu cuenta y después elegí el horario de tu asesoría estratégica.",
        path,
        robots: "noindex, follow",
      };
    default:
      break;
  }

  if (legalPage && LEGAL_SLUG_BY_PATH[path]) {
    const page = legalPage;
    const seo: PageSeo = {
      ...OG_IMAGE,
      title: `${page.title} | Lucentia Social`,
      description: page.description,
      path: page.path,
      keywords: en ? SEO_LEGAL_EN.keywords : SEO_LEGAL_ES.keywords,
    };
    return {
      ...seo,
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          { ...webPageNode(origin, locale, seo), dateModified: page.lastUpdated },
          breadcrumbNode(origin, locale, page.title, page.path),
        ],
      },
    };
  }

  if (path === "/admin") {
    return { ...SEO_ANALYTICS };
  }

  const notFound = en ? SEO_NOT_FOUND_EN : SEO_NOT_FOUND_ES;
  return { title: notFound.title, description: notFound.description, path, robots: "noindex, follow" };
}
